import { Invoice, Warning } from '@phosphor-icons/react';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { LoanWithReason } from '../../../../../../types/apiTypes';
import { isPastDate } from '../Loans';
import { formatDate } from '../../../../../../utils/DateUtils';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { DemoDataWrapper } from '../../home/components/DemoDataBadge';
import { useDemo } from '../../../../../../utils/DemoContext';

interface LoanWithReasonWithPaid extends LoanWithReason {
  paid?: number;
}

function LoanPaymentPlansWithChartsWidget({
  loans,
  isLoading,
  addClass,
  isDemoData = false,
}: {
  loans: LoanWithReasonWithPaid[];
  isLoading: boolean;
  addClass?: string;
  isDemoData?: boolean;
}) {
  const { screenView } = useScreenView();
  const [loansData, setLoansData] = useState<LoanWithReasonWithPaid[]>(loans);
  const navigate = useNavigate();
  const { isDemo } = useDemo();
  // Function to format numbers into currency
  const formatCurrency = (amount: number) => `$ ${amount.toLocaleString()}`;

  useEffect(() => {
    // Create a new array with updated loan objects
    const updatedLoans = loans.map((loan) => ({
      ...loan,
      current_balance: Number(loan?.current_balance ?? 0),
      total_cost_of_loan: Number(loan?.total_cost_of_loan ?? 0),
      paid:
        Number(loan?.total_cost_of_loan ?? 0) -
        Number(loan?.current_balance ?? 0),
    }));

    setLoansData(updatedLoans);
  }, [loans]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div
            className={`flex flex-col items-center gap-x-[16px] border-[1px] border-bordergray bg-white align-middle ${addClass} h-full min-h-full max-w-[1148px]`}
          >
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Cash Advance History
              </h2>
              <button
                onClick={
                  isDemo
                    ? undefined
                    : () => navigate('/dashboard/banking/cash-advances/active')
                }
                className={`text-[16px] font-[600] text-primary underline ${isDemo ? 'cursor-not-allowed opacity-50' : ''}`}
                disabled={isDemo}
              >
                Show All
              </button>
            </div>
            {!isLoading ? (
              <div className="w-full overflow-x-auto">
                {loansData && loansData?.length > 0 ? (
                  <table className="min-w-full table-auto rounded-lg bg-white shadow-md">
                    <thead className="bg-gray-100">
                      <tr>
                        {screenView !== 'mobile' && (
                          <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                            Purpose
                          </th>
                        )}
                        <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                          Paid
                        </th>
                        <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                          Left
                        </th>
                        {screenView !== 'mobile' && (
                          <th className="px-4 py-2 text-left text-[14px] font-[600] text-primary">
                            Progress
                          </th>
                        )}
                        <th className="px-4 py-2 text-right text-[14px] font-[600] text-primary">
                          Due Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loansData?.map((loan, index) => {
                        return (
                          loan?.total_cost_of_loan != null &&
                          loan?.end_date != null &&
                          loan?.current_balance != null && (
                            <tr
                              key={index}
                              className="border-b border-gray-200"
                            >
                              {screenView !== 'mobile' && (
                                <td className="px-4 py-2 text-[14px] text-primary">
                                  {loan?.loanRequest?.reason}
                                </td>
                              )}
                              <td className="px-4 py-2 text-right text-[14px] text-primary">
                                {formatCurrency(loan?.paid ?? 0)}
                              </td>
                              <td className="px-4 py-2 text-right text-[14px] text-primary">
                                {formatCurrency(loan?.current_balance)}
                              </td>
                              {screenView !== 'mobile' && (
                                <td className="h-10 min-w-[250px] max-w-[500px] px-4 py-2 text-right align-middle text-[14px] text-primary">
                                  <div className="flex w-full items-center justify-center">
                                    <div className="flex h-[12px] w-full flex-row overflow-hidden rounded-[10px] bg-[#E6E6E6]">
                                      {(loan?.paid ?? 0) > 0 &&
                                        loan?.total_cost_of_loan > 0 && (
                                          <div
                                            className={`h-full ${
                                              loan?.current_balance === 0
                                                ? 'bg-[#6A9E68]'
                                                : isPastDate(loan?.end_date)
                                                  ? 'bg-[#F7A826]'
                                                  : 'bg-[#1D213B]'
                                            }`}
                                            style={{
                                              width: `${Math.min(100, Math.max(0, ((loan?.paid ?? 0) / loan?.total_cost_of_loan) * 100))}%`,
                                            }}
                                          />
                                        )}
                                    </div>
                                  </div>
                                </td>
                              )}
                              <td className="px-4 py-2 text-right text-[14px] text-primary">
                                <span
                                  className={`flex items-center justify-end ${isPastDate(loan?.end_date) ? 'font-semibold text-red-500' : ''}`}
                                >
                                  {isPastDate(loan?.end_date) && (
                                    <Warning
                                      className="mr-2 text-orange-500"
                                      size={20}
                                    />
                                  )}
                                  {formatDate(
                                    loan?.end_date
                                      ? new Date(loan?.end_date)
                                      : new Date()
                                  )}
                                </span>
                              </td>
                            </tr>
                          )
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center space-y-2">
                    <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
                      <Invoice size={40} className="text-graysecondary" />
                    </div>
                    <p className="text-[14px] leading-[20px] text-graysecondary">
                      No Cash Advances Found
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex h-full min-h-[300px] w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={isLoading}
                  size={screenView === 'mobile' ? 40 : 60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="my-8 flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
    }
  };
  return (
    <DemoDataWrapper showBadge={isDemoData}>
      {renderScreenView(screenView)}
    </DemoDataWrapper>
  );
}

export default LoanPaymentPlansWithChartsWidget;
